import { type ReactNode, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Icon nhỏ mở một panel khi **rê chuột** hoặc **bấm** — tách từ `ChartHelp`.
 *
 * Dùng cho chú thích không cần chiếm chỗ thường trực: cách đọc biểu đồ, và từ
 * 2026-09-30 cả thông báo quyền riêng tư của bảng hiệu suất (người dùng yêu cầu
 * thu hai dải cảnh báo cam thành icon nhỏ, "bấm vào hoặc di chuột qua mới
 * show").
 *
 * Radix lo phần khó: đặt panel không tràn viewport, Escape để đóng, trả tiêu
 * điểm về nút. Phần của repo là **hover rồi ghim**:
 *
 * - Rê vào nút → mở tạm; rời nút (và rời panel) 120ms → đóng. Khoảng trễ để
 *   con trỏ kịp đi từ nút sang panel mà không đóng giữa chừng.
 * - Bấm khi đang mở tạm → **ghim** (không đóng); bấm lần nữa mới đóng.
 * - Mở bằng hover thì **không** kéo tiêu điểm vào panel — người đang gõ phím ở
 *   chỗ khác không bị cướp tiêu điểm chỉ vì con trỏ lướt qua.
 */
export function HoverPopover({
  label,
  icon,
  triggerClassName,
  contentClassName,
  children,
}: {
  /** Tên của nút cho trình đọc màn hình — icon một mình không có tên. */
  readonly label: string;
  readonly icon: ReactNode;
  readonly triggerClassName?: string | undefined;
  readonly contentClassName?: string | undefined;
  readonly children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const byHover = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = (): void => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };

  const openByHover = (): void => {
    cancelClose();
    byHover.current = true;
    setOpen(true);
  };

  const closeSoon = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) cancelClose();
        byHover.current = false;
        setOpen(next);
      }}
    >
      <PopoverTrigger
        type="button"
        aria-label={label}
        className={cn(
          'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          triggerClassName,
        )}
        onPointerEnter={openByHover}
        onPointerLeave={closeSoon}
        onFocus={openByHover}
        onBlur={closeSoon}
        /*
          ⚠️ Chặn cú bấm khi panel **đang mở sẵn nhờ hover**.

          Trigger của Radix bật/tắt mỗi lần bấm, mà trên máy tính thì con trỏ
          luôn đi qua nút trước khi bấm — nên thao tác tự nhiên nhất (rê vào,
          thấy nội dung, bấm để ghim lại mà đọc) lại **đóng** nó. Ở đây cú bấm
          đầu tiên chỉ đổi từ "mở tạm" sang "đã ghim"; bấm lần nữa mới đóng.
        */
        onClickCapture={(event) => {
          if (!open || !byHover.current) return;
          event.stopPropagation();
          byHover.current = false;
        }}
      >
        {icon}
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn('max-w-xs text-xs', contentClassName)}
        onOpenAutoFocus={(event) => {
          if (byHover.current) event.preventDefault();
        }}
        onPointerEnter={cancelClose}
        onPointerLeave={closeSoon}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
